import React, { useEffect, useMemo } from "react";
import { Icons } from "assets/icons";
import { usePermission } from "hooks/usePermission";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import {
  getAllPageContents,
  getSystemSettings,
} from "store/slices/pageContentSlice";
import { formatUTCDate } from "utils/utils";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import PageHeader from "components/common/PageHeader";

const Settings = () => {
  const { t } = useTranslation();
  const { edit } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit } = canAction("system_setting");

  const {
    loading,
    isLoading,
    loading2,
    pageContents,
    perPage,
    systemSettings,
    filters: { keyword, dates },
  } = useSelector((state) => state.PageContent);

  useEffect(() => {
    getAllData();
    dispatch(getSystemSettings());
  }, []);
  const systemSettingsData = systemSettings?.data;

  const getAllData = (page, action) => {
    const { actionType } = action || {};
    const payload = {
      perPage,
      pageNo: page || 1,
      status: -1,
      orderBy: -1,
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
    };
    dispatch(getAllPageContents(payload));
  };

  // Map API types to static config
  const PAGE_CONTENT_CONFIG = {
    TermsAndConditions: {
      key: "terms",
      title: t("Terms & Conditions"),
      path: "/settings/terms-and-condition",
    },
    PrivacyPolicy: {
      key: "Privacy Policy",
      title: t("Privacy Policy"),
      path: "/settings/privacy-policy",
    },
    FAQ: {
      key: "FAQ",
      title: t("Frequently asked questions"),
      path: "/settings/faq",
    },
  };

  // Static card for App Settings (not from API)
  const appSettingsCard = {
    key: "app",
    title: t("App Settings"),
    path: "/settings/app-settings",
    updatedAt: null,
    updatedBy: null,
  };

  // Static cards for config pages moved out of App Settings
  const howItWorksVideoCard = {
    key: "how-it-works-video",
    title: t("How It Works Video"),
    path: "/settings/configuration?type=how-it-works-video",
    updatedAt: null,
    updatedBy: null,
  };

  const ratingFeedbackCard = {
    key: "rating-feedback",
    title: t("Rating & Feedback Configuration"),
    path: "/settings/configuration?type=rating-feedback",
    updatedAt: null,
    updatedBy: null,
  };

  // Build dynamic list from API data
  const dynamicCards = useMemo(() => {
    // Always show every configured card, even if the API has no record yet
    return Object.entries(PAGE_CONTENT_CONFIG).map(([type, config]) => {
      const item = pageContents?.find((content) => content.type === type);
      return {
        ...config,
        updatedAt: item?.updatedAt || null,
        updatedBy: item?.updatedBy || null,
        _id: item?._id,
      };
    });
  }, [pageContents, t]);

  // Final list: dynamic cards first, then static App Settings
  const settingsList = [
    ...dynamicCards,
    appSettingsCard,
    howItWorksVideoCard,
    ratingFeedbackCard,
  ];
  const systemSettingsCardKeys = [
    "app",
    "how-it-works-video",
    "rating-feedback",
  ];
  const shimmerLayout = [
    {
      col: 4,
      blocks: [],
    },
    {
      col: 4,
      blocks: [],
    },
    {
      col: 4,
      blocks: [],
    },
    {
      col: 4,
      blocks: [],
    },
  ];

  if (loading || loading2) {
    return (
      <div className="page-content">
        <h2 className="page_title">{t("Settings")}</h2>
        <DetailsShimmer layout={shimmerLayout} />
      </div>
    );
  }

  return (
    <div className="page-content settings_page">
      <PageHeader title={t("Settings")} />

      <h3 className="page_sub_title">
        {t("Manage settings and configurations for the application")}
      </h3>

      <Row className="mt-3">
        {settingsList.map((item) => (
          <Col key={item.key} lg={4} md={6} sm={12}>
            <div
              className="settings_card"
              onClick={() => {
                if(!canEdit) return;
                navigate(item.path, {
                  state: systemSettingsCardKeys.includes(item.key)
                    ? { systemSettingsData }
                    : undefined,
                });
              }}
            >
              <div className="settings_card_header">
                <span>{item.title}</span>
                {canEdit && (
                  <img src={edit} alt="edit" width={20} height={20} />
                )}
              </div>

              <div className="settings_card_footer">
                <div>
                  <span className="label">{t("Last Edited On")}:</span>{" "}
                  {/* <span className="value">{formatUTCDate(item.updatedAt) || t("N/A")}</span> */}
                  <span className="value">
                    {systemSettingsCardKeys.includes(item.key)
                      ? formatUTCDate(systemSettings?.lastUpdatedAt)
                      : formatUTCDate(item?.updatedAt) || t("N/A")}
                  </span>
                </div>

                <div>
                  <span className="label">{t("By")}:</span>{" "}
                  <span className="value">
                    {systemSettingsCardKeys.includes(item.key)
                      ? systemSettings?.lastUpdatedBy?.name
                      : item?.updatedBy?.name || t("N/A")}
                  </span>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Settings;
